import { Container, Graphics, LINE_JOIN, Text } from "pixi.js"
import { FeedbackMood, SubmittedFeedback } from "game-common/models"
import { Keyboard } from "../input/keyboard"
import { GuiManager } from "../gui_manager"
import { getRendererApp } from "../renderer_app"
import { TextRenderer } from "../text/text_renderer"
import { chamfer, drawBoard, FACE, GOLD, groove, INK, MUTED, RIM, RIM_LIGHT, TIMBER_EDGE } from "./build_board"
import { createButton } from "./button_factory"
import { Modal } from "./modal"
import { PromptField } from "./prompt_field"
import { overlayRect, SearchField } from "./search_field"
import { UiButton } from "./ui_button"
import { getUiMetrics } from "./ui_metrics"

/**
 * The feedback composer: a word for the people making the game.
 *
 * Two ways in. The feedback rabbit asks, and the player says yes (the server sends a feedbackComposer
 * screen message carrying the visit, see campaigns/ember/ember_messenger); or the player presses the
 * feedback button themselves. It is the same board either way, cut timber like the build board and
 * the crafting panel, so it reads as part of the camp and not a browser form dropped over it.
 *
 * Asking for words alone gets few of them. So it opens on a single tap -- how is it going, three
 * ways -- and the words are optional once that is chosen, as is an address to answer them at.
 *
 * The text boxes are real DOM fields parked over the sockets the board draws (PromptField,
 * SearchField), which is what raises a phone's keyboard and gives autocorrect.
 */

export interface FeedbackComposerProps {
    source: "messenger" | "menu"
    // The visit that opened it, sent back with the feedback.
    visitId?: string
    onSubmit: (feedback: SubmittedFeedback) => void
    // Closed without sending: Not now, Escape, or the server taking it down.
    onCancel?: () => void
}

const MOODS: { mood: FeedbackMood; label: string }[] = [
    { mood: "loving", label: "Loving it" },
    { mood: "okay", label: "It's okay" },
    { mood: "stuck", label: "Frustrating" },
]

export const FEEDBACK_MAX_CHARS = 2000
const EMAIL_MAX_CHARS = 120

const W = 340
const PAD = 16
const INNER_W = W - PAD * 2
const TITLE_Y = 14
const SUBTITLE_Y = 34
const GROOVE_Y = 54
const MOOD_LABEL_Y = 64
const MOOD_Y = 78
const MOOD_H = 24
const MOOD_GAP = 6
const TEXT_Y = MOOD_Y + MOOD_H + 12
const TEXT_H = 84
const EMAIL_Y = TEXT_Y + TEXT_H + 8
const EMAIL_H = 24
const BUTTON_Y = EMAIL_Y + EMAIL_H + 14
const BUTTON_H = 26
const BUTTON_W = 92
const H = BUTTON_Y + BUTTON_H + PAD
const SOCKET_CHAMFER = 3
// How long the board takes to settle in, and how far it rises as it does.
const ARRIVE_MS = 180
const ARRIVE_RISE = 8
// The replay holds a sent board up this long with the player's words in it before it goes.
const REPLAY_SENT_HOLD_MS = 1600

export class FeedbackComposer {
    private guiManager: GuiManager
    private props: FeedbackComposerProps
    private surface = new Graphics()
    private board = new Graphics()
    private textSocket = new Graphics()
    private emailSocket = new Graphics()
    private moodButtons: UiButton[] = []
    private sendButton: UiButton
    private laterButton: UiButton
    private field: PromptField
    private email: SearchField
    private modal?: Modal
    private text = ""
    private emailText = ""
    private mood?: FeedbackMood
    private sent = false
    private openedAt = 0
    private fieldFocused = false
    private emailFocused = false
    private lastEmailKey = ""

    constructor(guiManager: GuiManager, props: FeedbackComposerProps) {
        this.guiManager = guiManager
        this.props = props
        this.field = new PromptField({
            placeholder: "What's working? What got in your way?",
            ariaLabel: "Your feedback",
            maxLength: FEEDBACK_MAX_CHARS,
            onInput: value => {
                this.text = value
                this.refreshSend()
            },
            onSubmit: () => this.send(),
            onClaimInput: () => this.guiManager.logic.playerController?.cancelAttackForUi?.(),
            suppressGameKeys: suppressed => Keyboard.suppressTyping(suppressed),
        })
        this.email = new SearchField({
            placeholder: "Email, if you'd like a reply (optional)",
            ariaLabel: "Your email address (optional)",
            onInput: value => (this.emailText = value.slice(0, EMAIL_MAX_CHARS)),
            onFocus: () => {
                this.emailFocused = true
                Keyboard.suppressTyping(true)
                this.drawSockets()
            },
            onBlur: () => {
                this.emailFocused = false
                Keyboard.suppressTyping(false)
                this.drawSockets()
            },
        })
        this.sendButton = createButton({ label: "Send", theme: "ember", shape: "chamfer", size: "small", bold: true,
            variant: "success", textColor: 0xdcffe2, width: BUTTON_W, height: BUTTON_H, onClick: () => this.send() })
        this.laterButton = createButton({ label: "Not now", theme: "ember", shape: "chamfer", size: "small",
            width: BUTTON_W, height: BUTTON_H, onClick: () => this.modal?.close() })
        this.build()
    }

    /** Put the board up. */
    open = () => {
        this.openedAt = performance.now()
        this.surface.alpha = 0
        this.modal = this.guiManager.createModal({
            padding: 0,
            width: W,
            height: H,
            content: this.surface,
            center: true,
            plain: true,
            backgroundAlpha: 0,
            hideCloseButton: true,
            closeTheme: "ember",
            closeCallback: () => this.closed(),
        })
        this.guiManager.logic.playerController?.client?.logActivity?.("Opened feedback composer")
    }

    close = () => this.modal?.close()

    isOpen = (): boolean => !!this.modal && !this.closedDown

    visitId = (): string | undefined => this.props.visitId

    /**
     * Once a frame while open: ease the board in, and keep the DOM fields parked over their sockets
     * (the canvas can be resized or letterboxed under them at any time).
     */
    update = () => {
        if (!this.isOpen()) return
        const t = Math.min(1, (performance.now() - this.openedAt) / ARRIVE_MS)
        const eased = 1 - Math.pow(1 - t, 3)
        this.surface.alpha = eased
        this.surface.pivot.y = -ARRIVE_RISE * (1 - eased)
        if (t < 1) {
            this.field.hide()
            this.email.hide()
            return
        }
        this.placeFields()
        const focused = this.field.isFocused()
        if (focused !== this.fieldFocused) {
            this.fieldFocused = focused
            this.drawSockets()
        }
    }

    /**
     * A session replay acting out what the recorded player sent: their words, their tap and their
     * address go into the board, Send lights, and the board goes after a moment -- so whoever watches
     * the replay reads what was said, where it was said.
     */
    actOutSent = (feedback: SubmittedFeedback) => {
        if (!this.isOpen()) return
        this.sent = true
        this.field.setValue(String(feedback?.feedback || "").slice(0, FEEDBACK_MAX_CHARS))
        this.email.show(this.emailRect() || { left: 0, top: 0, width: 0, height: 0 }, String(feedback?.email || ""))
        this.selectMood(MOODS.some(m => m.mood === feedback?.mood) ? feedback.mood : undefined)
        this.sendButton.setLabel("Sent ✓")
        this.sendButton.setEnabled(true)
        setTimeout(() => this.modal?.close(), REPLAY_SENT_HOLD_MS)
    }

    private closedDown = false

    private closed = () => {
        this.closedDown = true
        this.field.dispose()
        this.email.hide()
        this.disposeEmail()
        Keyboard.suppressTyping(false)
        if (!this.sent) this.props.onCancel?.()
    }

    private disposeEmail = () => {
        // SearchField has no dispose of its own; it is only ever hidden by the crafting panel, which
        // lives for the session. This board does not, so take its node off the page.
        const node = (this.email as any).node as HTMLElement | undefined
        node?.remove()
        ;(this.email as any).node = undefined
    }

    private send = () => {
        if (this.sent || !this.canSend()) return
        this.sent = true
        this.props.onSubmit({
            feedback: this.text.trim().slice(0, FEEDBACK_MAX_CHARS),
            email: this.emailText.trim(),
            mood: this.mood,
            source: this.props.source,
            visitId: this.props.visitId,
        })
        this.modal?.close()
    }

    private canSend = (): boolean => this.text.trim().length > 0 || !!this.mood

    private refreshSend = () => this.sendButton.setEnabled(this.canSend())

    private selectMood = (mood?: FeedbackMood) => {
        this.mood = mood
        this.moodButtons.forEach((button, i) => button.setSelected(MOODS[i].mood === mood))
        this.refreshSend()
    }

    private build = () => {
        drawBoard(this.board, W, H)
        groove(this.board, PAD, W - PAD, GROOVE_Y)
        this.surface.addChild(this.board)
        // Swallow presses on the board's bare wood, so a click between controls never reaches the world.
        this.surface.interactive = true
        this.surface.on("pointerdown", (event: any) => event?.stopPropagation?.())

        const title = new TextRenderer().role("compactTitle").color(INK).bold().render("A word for the makers") as Text
        title.position.set(PAD, TITLE_Y)
        const subtitle = new TextRenderer().role("caption").color(MUTED).render(this.props.source === "messenger"
            ? "Thistle will carry it straight to them."
            : "It goes straight to the people making the game.") as Text
        subtitle.position.set(PAD, SUBTITLE_Y)
        const moodLabel = new TextRenderer().role("overline").color(MUTED).render("SO FAR IT'S...") as Text
        moodLabel.position.set(PAD, MOOD_LABEL_Y)
        this.surface.addChild(title, subtitle, moodLabel)

        const moodW = (INNER_W - MOOD_GAP * (MOODS.length - 1)) / MOODS.length
        this.moodButtons = MOODS.map(({ mood, label }, i) => {
            const button = createButton({ label, theme: "ember", shape: "chamfer", size: "small",
                width: Math.floor(moodW), height: MOOD_H, accent: GOLD,
                onClick: () => this.selectMood(this.mood === mood ? undefined : mood) })
            button.position.set(PAD + i * (moodW + MOOD_GAP), MOOD_Y)
            this.surface.addChild(button)
            return button
        })

        this.textSocket.position.set(PAD, TEXT_Y)
        this.emailSocket.position.set(PAD, EMAIL_Y)
        this.surface.addChild(this.textSocket, this.emailSocket)
        this.drawSockets()

        this.laterButton.position.set(W - PAD - BUTTON_W * 2 - 8, BUTTON_Y)
        this.sendButton.position.set(W - PAD - BUTTON_W, BUTTON_Y)
        this.surface.addChild(this.laterButton, this.sendButton)
        this.refreshSend()
    }

    // The two sockets the DOM fields sit in: dug into the board, their lower lip lit gold while the
    // field has the keyboard (the Sprite Lab prompt's socket, for the same reason).
    private drawSockets = () => {
        const socket = (g: Graphics, h: number, focused: boolean) => {
            g.clear()
            g.lineStyle({ width: 1, color: focused ? GOLD : RIM, alpha: focused ? 0.9 : 0.55, join: LINE_JOIN.MITER })
            g.beginFill(TIMBER_EDGE, 0.9)
            chamfer(g, 0.5, 0.5, INNER_W - 1, h - 1, SOCKET_CHAMFER)
            g.endFill()
            g.lineStyle(0)
            g.beginFill(FACE, 0.6)
            chamfer(g, 2, 2, INNER_W - 4, h - 4, SOCKET_CHAMFER - 1)
            g.endFill()
            g.lineStyle(1, focused ? GOLD : RIM_LIGHT, focused ? 0.6 : 0.12)
            g.moveTo(3, h - 0.5).lineTo(INNER_W - 3, h - 0.5)
            g.lineStyle(0)
        }
        socket(this.textSocket, TEXT_H, this.fieldFocused)
        socket(this.emailSocket, EMAIL_H, this.emailFocused)
    }

    private pageRect = (socket: Container, h: number) => {
        let canvas: DOMRect | undefined
        let screen: { width: number; height: number } | undefined
        try {
            const app = getRendererApp()
            canvas = app.view.getBoundingClientRect()
            screen = { width: app.renderer.screen.width, height: app.renderer.screen.height }
        } catch (e) {
            return undefined
        }
        if (!canvas || !(screen.width > 0)) return undefined
        const start = socket.toGlobal({ x: 3, y: 3 })
        const end = socket.toGlobal({ x: INNER_W - 3, y: h - 3 })
        return overlayRect({ x: start.x, y: start.y, width: end.x - start.x, height: end.y - start.y }, screen, canvas)
    }

    private emailRect = () => this.pageRect(this.emailSocket, EMAIL_H)

    private placeFields = () => {
        const text = this.pageRect(this.textSocket, TEXT_H)
        if (text) this.field.show(text, getUiMetrics().touch)
        const email = this.emailRect()
        if (!email) return
        const key = `${Math.round(email.left)},${Math.round(email.top)},${Math.round(email.width)},${Math.round(email.height)}`
        if (key !== this.lastEmailKey) {
            this.lastEmailKey = key
            this.email.show(email, this.emailText)
        }
    }
}
